import React from 'react';
import { Alert, Badge, Form, Spinner, Toast, ToastContainer } from 'react-bootstrap';

import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import { ConciliationApi } from '../../Services/ConciliationApiDataService';
import {
  getConciliationKey,
  incorrectMatchErrorMessage,
  isIncorrectMatch,
  isReconciliationMatch
} from '../../Models/IncorrectMatchModels';

const EXPLANATION = 'This association will be marked as incorrect. The next reconciliation process must ignore this combination and look for a different association.';

export default function IncorrectMatchControl({
  row,
  conciliationType,
  getToken,
  onMarked,
  buttonLabel = 'Mark as incorrect',
  buttonClassName = 'btn btn-sm btn-outline-danger'
}) {
  const [show, setShow] = React.useState(false);
  const [reason, setReason] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');
  const [markedLocally, setMarkedLocally] = React.useState(false);
  const [showSuccess, setShowSuccess] = React.useState(false);
  const submittingRef = React.useRef(false);

  const alreadyMarked = markedLocally || isIncorrectMatch(row);
  const matched = isReconciliationMatch(row);
  const conciliationKey = getConciliationKey(row);

  const close = () => {
    if (submittingRef.current) return;
    setShow(false);
    setReason('');
    setError('');
  };

  const submit = async () => {
    if (submittingRef.current || alreadyMarked || !matched || !conciliationKey) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError('');

    try {
      const token = await getToken();
      const result = await ConciliationApi.markIncorrectMatch(
        conciliationType,
        conciliationKey,
        { reason: reason.trim() || null },
        token
      );
      setMarkedLocally(true);
      await onMarked?.(result, conciliationKey);
      setShow(false);
      setReason('');
      setShowSuccess(true);
    } catch (requestError) {
      setError(incorrectMatchErrorMessage(requestError));
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  if (!matched && !alreadyMarked) return null;

  return (
    <>
      {alreadyMarked ? (
        <Badge bg="danger">Incorrect match</Badge>
      ) : (
        <button
          type="button"
          className={buttonClassName}
          onClick={(event) => {
            event.stopPropagation();
            setShow(true);
          }}
          disabled={submitting}
        >
          {buttonLabel}
        </button>
      )}

      <ConfirmationModal
        show={show}
        onHide={close}
        onCancelClick={close}
        onAcceptClick={submit}
        title="Mark association as incorrect"
        acceptText={submitting ? 'Marking…' : 'Confirm'}
        cancelText="Cancel"
        acceptVariant="danger"
        acceptDisabled={submitting}
        cancelDisabled={submitting}
        closeDisabled={submitting}
        content={(
          <div>
            <p>{EXPLANATION}</p>
            <Form.Group controlId={`incorrect-match-reason-${conciliationType}-${conciliationKey}`}>
              <Form.Label>Reason (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                maxLength={1000}
                value={reason}
                disabled={submitting}
                onChange={(event) => setReason(event.target.value)}
              />
              <Form.Text className="d-flex justify-content-end">{reason.length}/1000</Form.Text>
            </Form.Group>
            {error && <Alert variant="danger" className="mt-3 mb-0">{error}</Alert>}
            {submitting && <div className="mt-3 text-muted"><Spinner size="sm" className="me-2" />Submitting…</div>}
          </div>
        )}
      />

      <ToastContainer position="top-end" className="position-fixed p-3" style={{ zIndex: 1100 }}>
        <Toast bg="success" show={showSuccess} onClose={() => setShowSuccess(false)} delay={4000} autohide>
          <Toast.Header><strong className="me-auto">Reconciliation</strong></Toast.Header>
          <Toast.Body className="text-white">The association was marked as incorrect.</Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
}
